export type SelectedVesselBlockName =
  | 'ports'
  | 'history'
  | 'route'
  | 'estimatedPosition'
  | 'weather'
  | 'inspections'
  | 'casualties'
  | 'dryDock'
  | 'particulars'
  | 'engine'
  | 'sales'
  | 'piClub';

export const SELECTED_VESSEL_BLOCKS: SelectedVesselBlockName[] = [
  'ports', 'route', 'estimatedPosition', 'weather', 'inspections',
  'casualties', 'dryDock', 'particulars', 'engine', 'sales', 'piClub'
];

export interface SelectedVesselBlock<T> {
  status: 'available' | 'unavailable';
  updatedAt?: string | null;
  data?: T;
  error?: { code: string; retryAfter?: string };
}

export interface SelectedVesselPortReference {
  uuid: string | null;
  unlocode: string | null;
  name: string | null;
}

export interface SelectedVesselSummary {
  imo: string | null;
  mmsi: string | null;
  uuid: string | null;
  name: string | null;
  aisName: string | null;
  callsign: string | null;
  countryIso: string | null;
  countryName: string | null;
  type: string | null;
  typeSpecific: string | null;
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  course: number | null;
  heading: number | null;
  navigationStatus: string | null;
  originalAisDestination: string | null;
  etaUtc: string | null;
  lastPositionUtc: string | null;
  currentDraught: number | null;
  departurePort: SelectedVesselPortReference;
  destinationPort: SelectedVesselPortReference;
  atdUtc: string | null;
  timezone: string | null;
  timezoneOffsetSeconds: number | null;
}

export interface SelectedVesselPort {
  uuid: string | null;
  name: string | null;
  unlocode: string | null;
  countryIso: string | null;
  countryName: string | null;
  type: string | null;
  latitude: number | null;
  longitude: number | null;
  areaLevel1: string | null;
  areaLevel2: string | null;
  terminals: Array<{
    code: string | null;
    name: string | null;
    latitude: number | null;
    longitude: number | null;
  }>;
}

export interface SelectedVesselPosition {
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  course: number | null;
  heading: number | null;
  destination: string | null;
  lastPositionUtc: string | null;
}

export interface SelectedVesselDetailsResponse {
  data: {
    summary: SelectedVesselSummary;
    blocks: Partial<{
      ports: SelectedVesselBlock<SelectedVesselPort[]>;
      history: SelectedVesselBlock<SelectedVesselPosition[]>;
      route: SelectedVesselBlock<{ distanceNm: number | null; coordinates: number[][] | null }>;
      estimatedPosition: SelectedVesselBlock<{ type: 'sat_e_estimate'; latitude: number | null; longitude: number | null }>;
      weather: SelectedVesselBlock<{
        time: string | null;
        temperatureCelsius: number | null;
        seaTemperatureCelsius: number | null;
        windSpeedKnots: number | null;
        windDirectionDegrees: number | null;
        waveHeightMeters: number | null;
        visibilityMeters: number | null;
        pressureHpa: number | null;
      }>;
      inspections: SelectedVesselBlock<Array<{
        inspectionDate: string | null;
        authority: string | null;
        port: string | null;
        type: string | null;
        deficiencyDescription: string | null;
        deficiencies: number | null;
        detained: boolean;
        updatedAt: string | null;
      }>>;
      casualties: SelectedVesselBlock<Array<{
        date: string | null;
        type: string | null;
        details: string | null;
        updatedAt: string | null;
      }>>;
      dryDock: SelectedVesselBlock<Array<{
        specialSurveyDate: string | null;
        dryDockDate: string | null;
        ioppIssueDate: string | null;
        ioppExpiryDate: string | null;
        updatedAt: string | null;
      }>>;
      particulars: SelectedVesselBlock<{
        vessel: {
          length: number | null;
          breadth: number | null;
          grossTonnage: number | null;
          deadweight: number | null;
          teu: number | null;
          yearBuilt: number | null;
          draughtAverage: number | null;
          draughtMaximum: number | null;
          speedAverage: number | null;
          speedMaximum: number | null;
        };
        classification: {
          code: string | null;
          designDeadweight: number | null;
          netTonnage: number | null;
          grossTonnage: number | null;
          designDraught: number | null;
          loa: number | null;
          lbp: number | null;
          depth: number | null;
          beam: number | null;
        };
      }>;
      engine: SelectedVesselBlock<Array<{
        designation: string | null;
        builder: string | null;
        designer: string | null;
        propulsionType: string | null;
        tradingCategory: string | null;
        maximumContinuousOutput: number | null;
        outputUnit: string | null;
        rpm: number | null;
        updatedAt: string | null;
      }>>;
      sales: SelectedVesselBlock<Array<{
        reportDate: string | null;
        type: string | null;
        destination: string | null;
        priceUsdMillions: number | null;
        priceUsdPerLdt: number | null;
        dryDockDate: string | null;
        specialSurveyDate: string | null;
        updatedAt: string | null;
      }>>;
      piClub: SelectedVesselBlock<{ name: string | null }>;
    }>;
  };
  retrievedAt: string;
}
